import React from "react";
import { Star, Quote } from "lucide-react";

export default function Testimonial({ name, rating, comment, image, productName }) {
  const [isHovered, setIsHovered] = React.useState(false);
  
  // Convert rating to number and handle potentially invalid values
  const numRating = parseFloat(rating) || 0;
  const fullStars = Math.floor(numRating);
  const hasHalfStar = numRating % 1 >= 0.5;

  // Get initials for avatar
  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Render stars
  const renderStars = () => {
    const stars = [];
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star
          key={`full-${i}`}
          size={14}
          className="text-amber-500 fill-amber-500"
        />
      );
    }
    
    // Add half star if needed
    if (hasHalfStar) {
      stars.push(
        <Star
          key="half"
          size={14}
          className="text-amber-500"
        />
      );
    }
    
    // Add empty stars
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star
          key={`empty-${i}`}
          size={14}
          className="text-amber-200"
        />
      );
    }
    
    return stars;
  };

  return (
    <div 
      className={`bg-white rounded-lg overflow-hidden shadow-md transition-all duration-300 border ${
        isHovered 
          ? "shadow-lg border-gray-200 translate-y-px" 
          : "shadow-sm border-gray-100"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Premium accent line with gradient animation */}
      <div 
        className={`h-0.5 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300 transition-all duration-500 ${
          isHovered ? "opacity-100" : "opacity-70"
        }`}
        style={{
          backgroundSize: isHovered ? "200% 100%" : "100% 100%",
          backgroundPosition: isHovered ? "right center" : "left center"
        }}
      />

      <div className="p-4 sm:p-5">
        {/* Product Image Section */}
        {image && (
          <div className="mb-3">
            <div className={`relative rounded-md overflow-hidden transition-all duration-300 ${
              isHovered ? "ring-2 ring-amber-400" : "ring-1 ring-amber-200"
            }`}>
              <img
                src={image}
                alt={productName || "Product"}
                className="w-full h-32 sm:h-40 object-cover bg-amber-50"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
              {productName && (
                <div className={`absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${
                  isHovered ? "opacity-100" : "opacity-80"
                }`}>
                  <p className="text-white text-xs font-medium truncate">
                    {productName}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="flex items-center space-x-3">
            {/* Reviewer Avatar with initials */}
            <div className={`h-10 w-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              isHovered 
                ? "bg-gradient-to-br from-amber-100 to-amber-300 border-2 border-amber-400" 
                : "bg-gradient-to-br from-amber-50 to-amber-200 border-2 border-amber-200"
            }`}>
              <span className="text-sm font-medium text-amber-800">
                {getInitials(name || "Anonymous")}
              </span>
            </div>
            
            <div>
              <h4 className="text-sm sm:text-base font-medium text-gray-900">
                {name || "Anonymous"}
              </h4>
              <p className="text-xs text-gray-500 mt-0.5 font-light">Verified Customer</p>
            </div>
          </div>
          
          {/* Rating Badge */}
          <div className={`flex items-center px-2 py-1 rounded-full transition-all duration-300 ${
            isHovered 
              ? "bg-gradient-to-r from-amber-100 to-amber-50 border border-amber-200" 
              : "bg-amber-50 border border-amber-100"
          }`}>
            <span className="text-amber-800 font-semibold text-sm mr-1">
              {numRating.toFixed(1)}
            </span>
            <div className="flex space-x-0.5">
              {renderStars()}
            </div>
          </div>
        </div>

        {/* Review Content */}
        {comment && (
          <div className="mt-3 relative">
            <Quote 
              size={16} 
              className={`absolute -left-1 -top-1 text-amber-200 transition-opacity duration-300 ${
                isHovered ? "opacity-100" : "opacity-60"
              }`} 
            />
            <p className="text-gray-700 text-xs sm:text-sm leading-relaxed font-light pl-4 italic">
              {comment}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}